import {
    Box,
    Card,
    CardContent,
    CardHeader,
    Chip,
    Skeleton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { useMemo } from 'react';
import ReactApexChart from 'react-apexcharts';
import { reportColors, styleReportChart } from '../shared/report-design';
import { formatUsd } from '../../system/report-formatters';

const DAY_ORDER = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const safeNum = (value) => {
    const num = Number(value);
    return Number.isFinite(num) ? num : 0;
};

const formatNumber = (value) => new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(safeNum(value));
const formatCurrency = (value) => formatUsd(safeNum(value));
const formatRatio = (value) => `${safeNum(value).toFixed(2)}x`;
const formatDecimal = (value, digits = 2) => safeNum(value).toFixed(digits);

const formatHourLabel = (value) => {
    const hour = safeNum(value);
    const normalized = ((hour % 24) + 24) % 24;
    const suffix = normalized >= 12 ? 'PM' : 'AM';
    const displayHour = normalized % 12 === 0 ? 12 : normalized % 12;
    return `${displayHour} ${suffix}`;
};

const normalizeFootfallPayload = (payload) => {
    if (!payload || typeof payload !== 'object') return null;
    if (payload.data && typeof payload.data === 'object') return payload.data;
    return payload;
};

export default function FootfallTab({ data, loading = false, surfaceCardSx = {}, showCost = true }) {
    const theme = useTheme();
    const payload = normalizeFootfallPayload(data);
    const totals = payload?.totals || {};

    const dayRows = useMemo(() => {
        const rows = Array.isArray(payload?.breakdowns?.byDayOfWeek) ? payload.breakdowns.byDayOfWeek : [];
        return rows
            .map((row) => ({
                label: row?.label || 'Unknown',
                footfall: safeNum(row?.footfall)
            }))
            .sort((a, b) => {
                const aIndex = DAY_ORDER.indexOf(a.label);
                const bIndex = DAY_ORDER.indexOf(b.label);
                return (aIndex === -1 ? 999 : aIndex) - (bIndex === -1 ? 999 : bIndex);
            });
    }, [payload]);

    const hourRows = useMemo(() => {
        const rows = Array.isArray(payload?.breakdowns?.byHourOfDay) ? payload.breakdowns.byHourOfDay : [];
        return rows
            .map((row) => ({
                hour: safeNum(row?.label),
                label: formatHourLabel(row?.label),
                footfall: safeNum(row?.footfall)
            }))
            .sort((a, b) => a.hour - b.hour);
    }, [payload]);

    const campaignRows = useMemo(() => {
        const rows = Array.isArray(payload?.campaigns) ? payload.campaigns : [];
        return rows.map((row, index) => {
            const metrics = row?.metrics || {};
            const tracker = row?.tracker || {};
            return {
                id: row?.campaignId || row?.campaignName || tracker?.id || `campaign-${index}`,
                campaignName: row?.campaignName || 'Campaign',
                channelType: row?.channelType || 'Unknown',
                trackerName: tracker?.name || 'Tracker',
                locationCount: safeNum(tracker?.locationCount),
                footfall: safeNum(metrics?.footfall),
                projectedFootfall: safeNum(metrics?.projectedFootfall),
                spend: safeNum(metrics?.spend),
                averageEcpv: safeNum(metrics?.averageEcpv),
                pValue: safeNum(metrics?.pValue),
                visitationLift: safeNum(metrics?.visitationLift),
                statisticallySignificant: Boolean(metrics?.statisticallySignificant),
                displayResult: Boolean(metrics?.displayResult),
                errors: Array.isArray(row?.errors) ? row.errors : []
            };
        });
    }, [payload]);

    const warningRows = useMemo(() => {
        const topLevel = Array.isArray(payload?.warnings) ? payload.warnings : [];
        const campaignWarnings = campaignRows.flatMap((row) => (
            row.errors.map((error, index) => ({
                label: `${row.campaignName} error ${index + 1}`,
                message: String(error)
            }))
        ));
        return [
            ...topLevel.map((message, index) => ({ label: `Warning ${index + 1}`, message: String(message) })),
            ...campaignWarnings
        ];
    }, [campaignRows, payload]);

    const topCampaign = campaignRows
        .slice()
        .sort((a, b) => b.footfall - a.footfall)[0];

    const bestLiftCampaign = campaignRows
        .slice()
        .filter((row) => row.visitationLift > 0)
        .sort((a, b) => b.visitationLift - a.visitationLift)[0];

    const bestValueCampaign = campaignRows
        .slice()
        .filter((row) => row.averageEcpv > 0)
        .sort((a, b) => a.averageEcpv - b.averageEcpv)[0];

    const metricCards = [
        { label: 'Tracked Campaigns', value: totals.trackedCampaigns, format: 'number', helper: 'Campaigns with visitation tracking' },
        { label: 'Footfall', value: totals.footfall, format: 'number', helper: 'Measured in-store visits' },
        { label: 'Projected Footfall', value: totals.projectedFootfall, format: 'number', helper: 'Modeled visit volume' },
        ...(showCost ? [
            { label: 'Spend', value: totals.spend, format: 'currency', helper: 'Tracked media spend' },
            { label: 'Average ECPV', value: totals.averageEcpv, format: 'currency', helper: 'Effective cost per visit' },
            { label: 'Projected ECPV', value: totals.projectedAverageEcpv, format: 'currency', helper: 'Modeled cost per visit' },
        ] : []),
        { label: 'Average p-Value', value: totals.averagePValue, format: 'decimal', helper: 'Confidence across tracked campaigns' },
        { label: 'Visit Lift', value: totals.averageVisitationLift, format: 'ratio', helper: 'Average visitation lift' },
    ];

    const summaryChips = [
        { label: 'Trackers', value: totals.trackers },
        { label: 'Locations', value: totals.locations },
        { label: 'Significant Campaigns', value: totals.statisticallySignificantCampaigns },
        { label: 'Programmatic Results', value: totals.displayResultCampaigns },
        { label: 'Campaign Errors', value: totals.campaignsWithErrors },
        { label: 'Tracker Errors', value: totals.trackerErrors },
    ];

    const dayChartOptions = useMemo(() => styleReportChart(theme, {
        chart: { type: 'bar', toolbar: { show: false }, zoom: { enabled: false } },
        plotOptions: { bar: { borderRadius: 8, columnWidth: '52%' } },
        colors: [reportColors.revenue],
        dataLabels: { enabled: false },
        xaxis: { categories: dayRows.map((row) => row.label) },
        yaxis: { labels: { formatter: (value) => formatNumber(value) } },
        tooltip: { y: { formatter: (value) => formatNumber(value) } },
        grid: { borderColor: alpha(theme.palette.primary.main, 0.08) }
    }), [dayRows, theme]);

    const hourChartOptions = useMemo(() => styleReportChart(theme, {
        chart: { type: 'line', toolbar: { show: false }, zoom: { enabled: false } },
        colors: [reportColors.conversions],
        dataLabels: { enabled: false },
        stroke: { curve: 'smooth', width: 3 },
        xaxis: { categories: hourRows.map((row) => row.label) },
        yaxis: { labels: { formatter: (value) => formatNumber(value) } },
        tooltip: { y: { formatter: (value) => formatNumber(value) } },
        grid: { borderColor: alpha(theme.palette.primary.main, 0.08) }
    }), [hourRows, theme]);

    const campaignChartOptions = useMemo(() => styleReportChart(theme, {
        chart: { type: 'bar', toolbar: { show: false }, zoom: { enabled: false } },
        plotOptions: { bar: { horizontal: true, borderRadius: 7, barHeight: '56%' } },
        dataLabels: { enabled: false },
        colors: [reportColors.revenue, reportColors.conversions],
        xaxis: {
            categories: campaignRows.map((row) => row.campaignName),
            labels: { formatter: (value) => formatNumber(value) }
        },
        tooltip: { y: { formatter: (value) => formatNumber(value) } },
        grid: { borderColor: alpha(theme.palette.primary.main, 0.08) }
    }), [campaignRows, theme]);

    const renderMetricValue = (metric) => {
        switch (metric.format) {
            case 'currency':
                return formatCurrency(metric.value);
            case 'decimal':
                return formatDecimal(metric.value);
            case 'ratio':
                return formatRatio(metric.value);
            default:
                return formatNumber(metric.value);
        }
    };

    if (loading && !payload) {
        return (
            <Box sx={{ pt: 1, display: 'grid', gap: 2 }}>
                <Box sx={{ display: 'grid', gap: 1, gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' } }}>
                    {Array.from({ length: 8 }).map((_, index) => (
                        <Skeleton key={`footfall-metric-${index}`} variant="rectangular" height={112} sx={{ ...surfaceCardSx, borderRadius: 3 }} />
                    ))}
                </Box>
                <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', xl: 'repeat(2, minmax(0, 1fr))' } }}>
                    <Skeleton variant="rectangular" height={320} sx={{ ...surfaceCardSx, borderRadius: 3 }} />
                    <Skeleton variant="rectangular" height={320} sx={{ ...surfaceCardSx, borderRadius: 3 }} />
                </Box>
                <Skeleton variant="rectangular" height={360} sx={{ ...surfaceCardSx, borderRadius: 3 }} />
            </Box>
        );
    }

    if (!payload) {
        return (
            <Box sx={{ pt: 1 }}>
                <Card elevation={0} sx={surfaceCardSx}>
                    <CardContent sx={{ p: 3 }}>
                        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                            Store Visit Insights
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            No footfall data is available for the selected range.
                        </Typography>
                    </CardContent>
                </Card>
            </Box>
        );
    }

    return (
        <Box sx={{ pt: 1, display: 'grid', gap: 2 }}>
            <Card elevation={0} sx={surfaceCardSx}>
                <CardContent sx={{ display: 'grid', gap: 2, p: { xs: 2, md: 2.5 } }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexDirection: { xs: 'column', md: 'row' } }}>
                        <Box sx={{ display: 'grid', gap: 0.5 }}>
                            <Typography variant="h5" sx={{ fontWeight: 800 }}>
                                Store Visit Insights
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Footfall, modeled visits, and visitation quality across tracked campaigns.
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {payload?.advertiser?.name || 'Advertiser'} · {payload?.date_range?.start || sessionStorage.getItem('sDate')} to {payload?.date_range?.end || sessionStorage.getItem('eDate')}
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'flex-start', justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
                            {summaryChips.map((chip) => (
                                <Chip key={chip.label} label={`${chip.label}: ${formatNumber(chip.value)}`} sx={{ bgcolor: alpha('#ffffff', 0.72) }} />
                            ))}
                        </Box>
                    </Box>

                    <Box sx={{ display: 'grid', gap: 1, gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' } }}>
                        {metricCards.map((metric) => (
                            <Box
                                key={metric.label}
                                sx={{
                                    borderRadius: 3,
                                    border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
                                    background: `linear-gradient(160deg, ${alpha('#ffffff', 0.76)} 0%, ${alpha('#ffffff', 0.52)} 100%)`,
                                    p: 1.5,
                                    display: 'grid',
                                    gap: 0.4
                                }}
                            >
                                <Typography variant="overline" sx={{ letterSpacing: '0.08em', color: 'text.secondary' }}>
                                    {metric.label}
                                </Typography>
                                <Typography variant="h4" sx={{ fontWeight: 800, fontSize: { xs: '1.65rem', md: '1.9rem' } }}>
                                    {renderMetricValue(metric)}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {metric.helper}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                </CardContent>
            </Card>

            <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', xl: 'repeat(2, minmax(0, 1fr))' } }}>
                <Card elevation={0} sx={surfaceCardSx}>
                    <CardHeader title="Footfall by Day" subheader="Weekly visitation pattern" />
                    <CardContent>
                        {dayRows.length ? (
                            <ReactApexChart
                                options={dayChartOptions}
                                series={[{ name: 'Footfall', data: dayRows.map((row) => row.footfall) }]}
                                type="bar"
                                height={320}
                            />
                        ) : (
                            <Typography variant="body2" color="text.secondary">No day-of-week footfall data.</Typography>
                        )}
                    </CardContent>
                </Card>
                <Card elevation={0} sx={surfaceCardSx}>
                    <CardHeader title="Footfall by Hour" subheader="Visit timing across the day" />
                    <CardContent>
                        {hourRows.length ? (
                            <ReactApexChart
                                options={hourChartOptions}
                                series={[{ name: 'Footfall', data: hourRows.map((row) => row.footfall) }]}
                                type="line"
                                height={320}
                            />
                        ) : (
                            <Typography variant="body2" color="text.secondary">No hour-of-day footfall data.</Typography>
                        )}
                    </CardContent>
                </Card>
            </Box>

            <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', xl: 'minmax(0, 1.45fr) minmax(320px, 0.9fr)' } }}>
                <Card elevation={0} sx={surfaceCardSx}>
                    <CardHeader title="Campaign Footfall" subheader="Measured vs projected store visits" />
                    <CardContent>
                        {campaignRows.length ? (
                            <ReactApexChart
                                options={campaignChartOptions}
                                series={[
                                    { name: 'Footfall', data: campaignRows.map((row) => row.footfall) },
                                    { name: 'Projected', data: campaignRows.map((row) => row.projectedFootfall) }
                                ]}
                                type="bar"
                                height={Math.max(320, campaignRows.length * 64)}
                            />
                        ) : (
                            <Typography variant="body2" color="text.secondary">No campaign footfall data.</Typography>
                        )}
                    </CardContent>
                </Card>

                <Card elevation={0} sx={surfaceCardSx}>
                    <CardHeader title="Visit Quality Snapshot" subheader="Quick readout of the strongest visit signals" />
                    <CardContent sx={{ display: 'grid', gap: 1.5 }}>
                        <Box sx={{ p: 1.5, borderRadius: 2, border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}` }}>
                            <Typography variant="caption" color="text.secondary">Top Footfall Campaign</Typography>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{topCampaign?.campaignName || '—'}</Typography>
                            <Typography variant="body2" color="text.secondary">
                                {topCampaign ? `${formatNumber(topCampaign.footfall)} visits · ${topCampaign.channelType}` : 'No campaign footfall recorded.'}
                            </Typography>
                        </Box>
                        <Box sx={{ p: 1.5, borderRadius: 2, border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}` }}>
                            <Typography variant="caption" color="text.secondary">Best Visitation Lift</Typography>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{bestLiftCampaign?.campaignName || '—'}</Typography>
                            <Typography variant="body2" color="text.secondary">
                                {bestLiftCampaign ? `${formatRatio(bestLiftCampaign.visitationLift)} lift · p=${formatDecimal(bestLiftCampaign.pValue)}` : 'No lift signal available.'}
                            </Typography>
                        </Box>
                        {showCost ? (
                            <Box sx={{ p: 1.5, borderRadius: 2, border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}` }}>
                                <Typography variant="caption" color="text.secondary">Lowest Cost per Visit</Typography>
                                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{bestValueCampaign?.campaignName || '—'}</Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {bestValueCampaign ? `${formatCurrency(bestValueCampaign.averageEcpv)} average ECPV` : 'No ECPV data available.'}
                                </Typography>
                            </Box>
                        ) : null}
                        <Box sx={{ p: 1.5, borderRadius: 2, border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}` }}>
                            <Typography variant="caption" color="text.secondary">Confidence Overview</Typography>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                {formatNumber(totals.statisticallySignificantCampaigns)} of {formatNumber(totals.trackedCampaigns)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Campaigns with statistically significant footfall results.
                            </Typography>
                        </Box>
                    </CardContent>
                </Card>
            </Box>

            <Card elevation={0} sx={surfaceCardSx}>
                <CardHeader title="Campaign Visit Table" subheader="Campaign-level visitation detail for the selected range." />
                <CardContent sx={{ p: 0 }}>
                    {campaignRows.length ? (
                        <TableContainer sx={{ overflowX: 'auto' }}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Campaign</TableCell>
                                        <TableCell>Channel</TableCell>
                                        <TableCell>Tracker</TableCell>
                                        <TableCell align="right">Locations</TableCell>
                                        <TableCell align="right">Footfall</TableCell>
                                        <TableCell align="right">Projected</TableCell>
                                        {showCost ? <TableCell align="right">Spend</TableCell> : null}
                                        {showCost ? <TableCell align="right">Avg ECPV</TableCell> : null}
                                        <TableCell align="right">p-Value</TableCell>
                                        <TableCell align="right">Lift</TableCell>
                                        <TableCell>Status</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {campaignRows.map((row) => (
                                        <TableRow key={row.id} hover>
                                            <TableCell>
                                                <Typography variant="body2" sx={{ fontWeight: 700 }}>{row.campaignName}</Typography>
                                            </TableCell>
                                            <TableCell>{row.channelType}</TableCell>
                                            <TableCell>{row.trackerName}</TableCell>
                                            <TableCell align="right">{formatNumber(row.locationCount)}</TableCell>
                                            <TableCell align="right">{formatNumber(row.footfall)}</TableCell>
                                            <TableCell align="right">{formatNumber(row.projectedFootfall)}</TableCell>
                                            {showCost ? <TableCell align="right">{formatCurrency(row.spend)}</TableCell> : null}
                                            {showCost ? <TableCell align="right">{formatCurrency(row.averageEcpv)}</TableCell> : null}
                                            <TableCell align="right">{formatDecimal(row.pValue)}</TableCell>
                                            <TableCell align="right">{formatRatio(row.visitationLift)}</TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                                    {row.statisticallySignificant ? <Chip size="small" color="success" label="Significant" /> : null}
                                                    {row.displayResult ? <Chip size="small" color="primary" label="Programmatic Result" /> : null}
                                                    {row.errors.length ? <Chip size="small" color="warning" label={`${row.errors.length} Error${row.errors.length > 1 ? 's' : ''}`} /> : null}
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    ) : (
                        <Box sx={{ px: 3, pb: 3 }}>
                            <Typography variant="body2" color="text.secondary">
                                No campaign visitation details are available.
                            </Typography>
                        </Box>
                    )}
                </CardContent>
            </Card>

            {warningRows.length ? (
                <Card elevation={0} sx={surfaceCardSx}>
                    <CardHeader title="Warnings" subheader="Issues or notices returned with the visitation data." />
                    <CardContent sx={{ display: 'grid', gap: 1.25 }}>
                        {warningRows.map((warning) => (
                            <Box
                                key={`${warning.label}-${warning.message}`}
                                sx={{
                                    p: 1.5,
                                    borderRadius: 2,
                                    border: `1px solid ${alpha(theme.palette.warning.main, 0.25)}`,
                                    backgroundColor: alpha(theme.palette.warning.main, 0.08)
                                }}
                            >
                                <Typography variant="caption" color="text.secondary">{warning.label}</Typography>
                                <Typography variant="body2">{warning.message}</Typography>
                            </Box>
                        ))}
                    </CardContent>
                </Card>
            ) : null}
        </Box>
    );
}
